import { addDays, rankProjectsByUsage, USAGE_WINDOW_DAYS, type LiveProject } from '@wiztime/core';
import { queries } from '@wiztime/storage';
import { useMemo, useState } from 'react';
import { useLive } from '../../hooks/useLive';
import { useProjects, type ProjectsView } from '../../hooks/useProjects';
import { useToday } from '../../hooks/useRouteView';
import { useStore } from '../../hooks/useServices';
import { useDayUi } from './dayUiStore';

/** `projects` in the order of `order`; ids missing from it (added since) follow, in their own order. */
export function sortByOrder(projects: readonly LiveProject[], order: readonly string[]): LiveProject[] {
  const position = new Map(order.map((id, index) => [id, index]));
  return [...projects].sort(
    (a, b) => (position.get(a.id) ?? order.length) - (position.get(b.id) ?? order.length),
  );
}

/**
 * The active projects ordered for choosing: most used in the last 14 days first. While `frozen`
 * the order stays as it was when freezing began, because recording a block changes the usage and a
 * list must not rearrange itself under the pointer.
 */
export function useRankedActiveProjects(
  active: readonly LiveProject[],
  frozen: boolean,
): readonly LiveProject[] {
  const store = useStore();
  const today = useToday();
  const recent = useLive(
    () => queries.blocksInRange(store.repo, addDays(today, 1 - USAGE_WINDOW_DAYS), today),
    [store, today],
  );
  const ranked = useMemo(
    () => (recent.data === undefined ? active : rankProjectsByUsage(active, recent.data, today)),
    [active, recent.data, today],
  );

  const [frozenOrder, setFrozenOrder] = useState<readonly string[] | null>(null);
  if (frozen && frozenOrder === null && recent.data !== undefined) {
    setFrozenOrder(ranked.map((project) => project.id));
  }
  if (!frozen && frozenOrder !== null) setFrozenOrder(null);

  return useMemo(
    () => (frozen && frozenOrder !== null ? sortByOrder(active, frozenOrder) : ranked),
    [frozen, frozenOrder, active, ranked],
  );
}

/**
 * `useProjects` with `active` ordered by recent use, for the day and week views: the project
 * picker and the quick-mark card offer projects in this order. Frozen while a picker, the conflict
 * resolver or a quick-mark project is open.
 */
export function useProjectsForChoosing(): ProjectsView {
  const projects = useProjects();
  const frozen = useDayUi(
    (state) => state.picker !== null || state.conflict !== null || state.quickMark.projectId !== null,
  );
  const active = useRankedActiveProjects(projects.active, frozen);
  return useMemo(() => ({ ...projects, active }), [projects, active]);
}
